import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaPlay,
  FaPause,
  FaStop,
  FaMinus,
  FaPlus,
  FaTrash,
  FaSave,
  FaTimes,
  FaVolumeUp,
  FaVolumeMute,
  FaUndo,
  FaRedo,
  FaClock,
  FaCircle,
} from "react-icons/fa";
import InstrumentAudioContext from "./InstrumentAudioContext";
import { Client, Databases, ID, Query } from "appwrite";
import { useUser } from "./UserContext";
import "./css/BeatSeq.css";
import MelodyModal from "./MelodyModal";
import PianoRollModal from "./PianoRollModal";
import { ClipLoader } from "react-spinners";
import genrePatterns from "./GenrePattern";
import client from "./appwriteConfig";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { usePatternInsSeq } from "./PatternInsSeqContext";

// Initialize Appwrite client
const databases = new Databases(client);

const generateNotes = () => {
  const notes = [];
  const noteNames = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];
  for (let octave = 0; octave < 6; octave++) {
    for (const note of noteNames) {
      notes.push(`${note}${octave}`);
    }
  }
  return notes; // Reverse to start from C0 at the bottom
};

const BeatSeq = ({
  pads,
  sequence,
  setSequence,
  togglePlay,
  bpm,
  setBpm,
  updateInstruments,
  onStepChange,
  onGlobalPlayChange,
  transformedData,
  editSongPatternNumber,
  setEditSongPatternNumber,
  onAddKit,
  isRecording,
  setIsRecording,
  handleMapButtonClick,
  handleClockButtonClick,
  trialDropdownLocked = false,
  openPlansModal = () => {},
  trialSeconds = 0,
  isTrial = false,
  stepCount = 16,
  setStepCount,
  overwriteMode = false,
  setOverwriteMode,
}) => {
  const [pianoRollPatternNo, setPianoRollPatternNo] = useState(null);
  const { user } = useUser();
  const [selectedPattern, setSelectedPattern] = useState("initial");
  const [selectedInstruments, setSelectedInstruments] = useState([
    { InsName: "Guitar", PianoRollPatternNo: "", InsVolume: 0.5 },
  ]);
  const [isSwingOn, setIsSwingOn] = useState(false);
  const swingAmount = 0.35; // 0.25 mild, 0.35 nice, 0.5 heavy

  const [sequences, setSequences] = useState(
    pads.map(() => Array(stepCount).fill(false)),
  );
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [kitMuteStates, setKitMuteStates] = useState(
    pads.reduce((acc, pad) => {
      acc[pad.kitUrl] = false;
      return acc;
    }, {}),
  );

  const [instrumentMuteStates, setInstrumentMuteStates] = useState(
    selectedInstruments.reduce((acc, instrument) => {
      acc[instrument.InsName] = false;
      return acc;
    }, {}),
  );
  const [selectedGenre, setSelectedGenre] = useState("");
  const [previewInstrumentByOriginal, setPreviewInstrumentByOriginal] =
    useState({});
  const intervalRef = useRef(null);
  const stepsRefs = useRef(pads.map(() => React.createRef()));
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [kitVolumes, setKitVolumes] = useState({});
  const {
    fetchBeatPatterns,
    beatPatterns,
    setBeatPatterns,
    saveBeatPattern,
    deleteBeatPattern,
  } = usePatternInsSeq();

  const [selectedInstrument, setSelectedInstrument] = useState("");
  const [modalInstrument, setModalInstrument] = useState("Guitar");
  const [modalInstrumentIndex, setModalInstrumentIndex] = useState(0);
  const [pianoRollInstrument, setPianoRollInstrument] = useState(null);
  const [gridState, setGridState] = useState({});
  const [instrumentVolumes, setInstrumentVolumes] = useState({});
  const [isVolumeChanging, setIsVolumeChanging] = useState(false);
  const notes = generateNotes();
  const [currentBar, setCurrentBar] = useState(0);
  const [melodyInstrumentPatternNo, setMelodyInstrumentPatternNo] =
    useState(null);
  const globalIntervalRef = useRef(null);
  const [isGlobalPlaying, setIsGlobalPlaying] = useState(false);
  const [loadedMelodyData, setLoadedMelodyData] = useState({});
  const [virtualGrids, setVirtualGrids] = useState({});
  const [globalActiveNotesByInstrument, setGlobalActiveNotesByInstrument] =
    useState({});
  const {
    pianoRollPatterns,
    setPianoRollPatterns,
    fetchPianoRollPatterns,
    fetchNotesPianoRoll,
  } = usePatternInsSeq();
  const [kitButtonActive, setKitButtonActive] = useState(null);
  const normalizeSteps = (arr, fill = false, targetLength = stepCount) => {
    const safeArr = Array.isArray(arr) ? arr : [];

    if (!safeArr.length) {
      return Array(targetLength).fill(fill);
    }

    // When a 16-step pattern is displayed as 32 steps,
    // repeat steps 1–16 into steps 17–32.
    if (safeArr.length === 16 && targetLength === 32) {
      return [...safeArr, ...safeArr];
    }

    // General repeating support, including a possible future 64-step mode.
    return Array(targetLength)
      .fill()
      .map((_, index) => {
        const value = safeArr[index % safeArr.length];
        return value !== undefined ? value : fill;
      });
  };

  const makeEmptySequence = () => Array(stepCount).fill(false);

  const makeEmptyGrid = () =>
    Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false));

  const toggleStepCount = () => {
    setStepCount((prev) => (prev === 16 ? 32 : 16));
  };
  const instruments = [
    "Piano",
    "String",
    "Pads",
    "Pluck",
    "EPiano",
    "Guitar",
    "Voice",
    "Flute",
    "Saxophone",
    "Clarinet",
    "Trumpet",
    "Brass",
  ];
  const getSwingDelay = (step) => {
    if (!isSwingOn) return 0;

    // delay off-beat 16th steps: 2,4,6... visually index 1,3,5...
    const isOffBeatStep = step % 2 === 1;

    if (!isOffBeatStep) return 0;

    const stepInterval = (60 / bpm) * 250;
    return stepInterval * swingAmount;
  };
  useEffect(() => {
    if (!transformedData) return;

    const incomingPatterns = transformedData.patterns || transformedData;
    const overwriteMode = transformedData.overwriteMode || false;

    setSequences((prevSequences) =>
      pads.map((pad, index) => {
        const kitName = pad.kitName.toLowerCase();
        const recordedKit = incomingPatterns[kitName];

        const oldSequence =
          prevSequences[index] || Array(stepCount).fill(false);

        const normalizedOld = normalizeSteps(
          oldSequence.map(Boolean),
          false,
          stepCount,
        );
        if (!recordedKit?.sequence) {
          return normalizedOld;
        }
        const recordedSequence = normalizeSteps(
          recordedKit.sequence.map(Boolean),
          false,
          stepCount,
        );
        if (overwriteMode) {
          return recordedSequence;
        }

        return normalizedOld.map(
          (oldBeat, i) => oldBeat || recordedSequence[i],
        );
      }),
    );
  }, [transformedData, pads, stepCount]);
  useEffect(() => {
    setInstrumentMuteStates(
      selectedInstruments.reduce((acc, instrument) => {
        acc[instrument.InsName] = false;
        return acc;
      }, {}),
    );
  }, [selectedInstruments]);
  useEffect(() => {
    const fetchPatterns = async () => {
      const patterns = await fetchPianoRollPatterns();
      setPianoRollPatterns(patterns);
    };
    fetchPatterns();
  }, []);
  useEffect(() => {
    if (modalInstrumentIndex === null) return;

    const matchingInstrument = selectedInstruments[modalInstrumentIndex];

    if (!matchingInstrument) {
      setModalInstrument(null);
      setModalInstrumentIndex(null);
      setMelodyInstrumentPatternNo("");
      return;
    }

    // Automatically reflect the instrument name from this row.
    setModalInstrument(matchingInstrument.InsName);

    // Automatically reflect the preset belonging to this row.
    setMelodyInstrumentPatternNo(matchingInstrument.PianoRollPatternNo || "");
  }, [selectedInstruments, modalInstrumentIndex]);
  useEffect(() => {
    if (user) {
      fetchBeatPatterns();
    }
  }, [user]);

  useEffect(() => {
    InstrumentAudioContext.setBpm(bpm);
  }, [bpm]);

  useEffect(() => {
    const loadAudioForInstruments = async () => {
      for (const instrument of selectedInstruments) {
        await InstrumentAudioContext.loadAudio(instrument.InsName);
      }
    };

    loadAudioForInstruments();
  }, [selectedInstruments]);

  useEffect(() => {
    selectedInstruments.forEach((instrument) => {
      InstrumentAudioContext.registerVolumeChangeListener(
        instrument.InsName,
        (newVolume) => {
          setInstrumentVolumes((prevVolumes) => ({
            ...prevVolumes,
            [instrument.InsName]: newVolume,
          }));
        },
      );
    });
  }, [selectedInstruments]);

  useEffect(() => {
    pads.forEach((pad) => {
      InstrumentAudioContext.registerVolumeChangeListener(
        pad.kitUrl,
        (newVolume) => {
          setKitVolumes((prevVolumes) => ({
            ...prevVolumes,
            [pad.kitUrl]: newVolume,
          }));
        },
      );
    });
  }, [pads]);

  useEffect(() => {
    pads.forEach((pad) => {
      InstrumentAudioContext.preloadAudio(pad.kitUrl);
    });
  }, [pads]);

  useEffect(() => {
    selectedInstruments.forEach((instrument) => {
      InstrumentAudioContext.stopAllNotes();
    });

    return () => {
      InstrumentAudioContext.stopAllNotes();
    };
  }, [selectedInstruments, bpm]);

  useEffect(() => {
    const preloadAllAudio = async () => {
      // Preload drum kit audio
      await Promise.all(
        pads.map((pad) => InstrumentAudioContext.preloadAudio(pad.kitUrl)),
      );

      // Preload instrument audio
      await Promise.all(
        selectedInstruments.map((instrument) =>
          InstrumentAudioContext.loadAudio(instrument.InsName),
        ),
      );
    };

    preloadAllAudio();
  }, [pads, selectedInstruments]);
  const handleInstrumentChangeFromDropdown = async (
    currentInstrument,
    newInstrument,
    instrumentIndex = null,
  ) => {
    if (!newInstrument || currentInstrument === newInstrument) return;

    const changedInstrument = selectedInstruments.find(
      (inst) => inst.InsName === currentInstrument,
    );

    setSelectedInstruments((prevInstruments) =>
      prevInstruments.map((inst) =>
        inst.InsName === currentInstrument
          ? {
              ...inst,
              InsName: newInstrument,
            }
          : inst,
      ),
    );

    // Move the grid from the old instrument name to the new name.
    setGridState((previous) => {
      const updated = { ...previous };

      if (updated[currentInstrument]) {
        updated[newInstrument] = updated[currentInstrument];
        delete updated[currentInstrument];
      }

      return updated;
    });

    setVirtualGrids((previous) => {
      const updated = { ...previous };

      if (updated[currentInstrument]) {
        updated[newInstrument] = updated[currentInstrument];
        delete updated[currentInstrument];
      }

      return updated;
    });

    setLoadedMelodyData((previous) => {
      const updated = { ...previous };

      if (updated[currentInstrument]) {
        updated[newInstrument] = {
          ...updated[currentInstrument],
          instrumentName: newInstrument,
        };

        delete updated[currentInstrument];
      }

      return updated;
    });

    setPreviewInstrumentByOriginal((previous) => {
      const updated = { ...previous };

      if (updated[currentInstrument]) {
        updated[newInstrument] = updated[currentInstrument];
        delete updated[currentInstrument];
      }

      return updated;
    });

    // Keep an opened Melody Modal synchronized.
    if (
      modalInstrumentIndex !== null &&
      instrumentIndex === modalInstrumentIndex
    ) {
      setModalInstrument(newInstrument);

      setMelodyInstrumentPatternNo(changedInstrument?.PianoRollPatternNo || "");
    }

    InstrumentAudioContext.stopAllNotes();
    await InstrumentAudioContext.loadAudio(newInstrument);
  };
  const handleMelodyInstrumentChange = async (oldInstrument, newInstrument) => {
    await handleInstrumentChangeFromDropdown(oldInstrument, newInstrument);
  };
  useEffect(() => {
    setSequences((prev) =>
      pads.map((pad, index) => normalizeSteps(prev[index], false)),
    );

    setGridState((prev) => {
      const updated = {};
      Object.keys(prev).forEach((instrumentName) => {
        updated[instrumentName] = normalizeSteps(
          prev[instrumentName],
          null,
        ).map((row) => row || Array(notes.length).fill(false));
      });
      return updated;
    });

    setVirtualGrids((prev) => {
      const updated = {};
      Object.keys(prev).forEach((instrumentName) => {
        updated[instrumentName] = normalizeSteps(
          prev[instrumentName],
          null,
        ).map((row) => row || Array(notes.length).fill(false));
      });
      return updated;
    });
  }, [stepCount, pads.length]);
  const handleKitButtonClick = (kitIndex) => {
    if (kitButtonActive === kitIndex) {
      setKitButtonActive(null);
    } else {
      if (kitButtonActive !== null) {
        const newSequences = [...sequences];
        newSequences[kitIndex] = [...sequences[kitButtonActive]];
        addToHistory(newSequences);
      }
      setKitButtonActive(kitIndex);
    }
  };

  const toggleBeat = (padIndex, beatIndex) => {
    setSequences((prevSequences) => {
      const newSequences = prevSequences.map((sequence, index) =>
        index === padIndex
          ? sequence.map((beat, idx) => (idx === beatIndex ? !beat : beat))
          : sequence,
      );
      addToHistory(newSequences); // Add to history for undo/redo
      return newSequences;
    });
  };
  const buildRepeatingGridFromNotes = (
    notesArray,
    targetLength = stepCount,
  ) => {
    const parsedNotes = (notesArray || [])
      .map((savedNote) => {
        const [noteName, barValue] = savedNote.split(",");
        const sourceStep = parseInt(barValue, 10) - 1;
        const noteIndex = notes.findIndex((note) => note === noteName);

        return {
          noteIndex,
          sourceStep,
        };
      })
      .filter(
        ({ noteIndex, sourceStep }) => noteIndex !== -1 && sourceStep >= 0,
      );

    const highestSavedStep = parsedNotes.reduce(
      (highest, item) => Math.max(highest, item.sourceStep + 1),
      0,
    );

    // Presets occupying only steps 1–16 are treated as 16-step presets.
    // Existing 32-step presets remain 32-step presets.
    const sourceLength = highestSavedStep > 16 ? 32 : 16;

    const newGrid = Array(targetLength)
      .fill()
      .map(() => Array(notes.length).fill(false));

    parsedNotes.forEach(({ noteIndex, sourceStep }) => {
      for (
        let targetStep = sourceStep;
        targetStep < targetLength;
        targetStep += sourceLength
      ) {
        newGrid[targetStep][noteIndex] = true;
      }
    });

    return newGrid;
  };
  const fetchNotesAndSetGrid = async (
    instrument,
    patternNo,
    targetLength = stepCount,
  ) => {
    if (!patternNo) {
      const emptyGrid = Array(targetLength)
        .fill()
        .map(() => Array(notes.length).fill(false));

      setGridState((previous) => ({
        ...previous,
        [instrument.InsName]: emptyGrid,
      }));

      return emptyGrid;
    }

    const notesArray = await fetchNotesPianoRoll(patternNo);

    const newGrid = buildRepeatingGridFromNotes(notesArray, targetLength);

    setGridState((previous) => ({
      ...previous,
      [instrument.InsName]: newGrid,
    }));

    return newGrid;
  };
  const handleMelodyInstrumentPreview = async (
    originalInstrument,
    previewInstrument,
  ) => {
    await InstrumentAudioContext.loadAudio(previewInstrument);

    InstrumentAudioContext.stopAllNotes();

    setPreviewInstrumentByOriginal((prev) => {
      if (previewInstrument === originalInstrument) {
        const updated = { ...prev };
        delete updated[originalInstrument];
        return updated;
      }

      return {
        ...prev,
        [originalInstrument]: previewInstrument,
      };
    });
  };

  const handleUserPatternChange = async (event) => {
    const patternId = event.target.value;

    setSelectedPattern(patternId);

    if (patternId === "initial") {
      return;
    }

    try {
      const response = await databases.getDocument(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_BEAT_PATTERN_ID,
        patternId,
      );

      let parsedKits = {};

      try {
        parsedKits = JSON.parse(response.kitsJson || "{}");
      } catch (parseError) {
        console.error("Failed to parse kitsJson:", parseError);
        parsedKits = {};
      }

      /*
      Old patterns that do not have patternSize automatically use 16.
      Only an explicitly saved value of 32 will load as 32.
    */
      const savedPatternSize = Number(response.patternSize);

      const targetStepCount = savedPatternSize === 32 ? 32 : 16;

      /*
      Automatically update the 16/32 button according to
      the selected pattern.
    */
      if (stepCount !== targetStepCount) {
        setStepCount(targetStepCount);
      }

      let parsedInstruments = [];

      try {
        const rawInstruments = JSON.parse(response.instrumentsJson || "{}");

        /*
        instrumentsJson is currently saved as an object:

        {
          Guitar: {
            InsName: "Guitar",
            PianoRollPatternNo: 1,
            InsVolume: 0.5
          }
        }

        Convert it into an array before using .map().
      */
        parsedInstruments = Array.isArray(rawInstruments)
          ? rawInstruments
          : Object.values(rawInstruments || {});
      } catch (parseError) {
        console.error("Failed to parse instrumentsJson:", parseError);
        parsedInstruments = [];
      }

      const newKitVolumes = {};

      const newSequences = pads.map((pad, index) => {
        const kitName = pad.kitName || `pad-${index}`;

        const kitData = parsedKits[kitName] || {
          sequence: Array(targetStepCount).fill(0),
          kitVolume: 0.5,
        };

        const volume =
          kitData.kitVolume !== undefined ? Number(kitData.kitVolume) : 0.5;

        InstrumentAudioContext.setKitVolume(volume, pad.kitUrl);

        newKitVolumes[pad.kitUrl] = volume;

        const savedSequence = Array.isArray(kitData.sequence)
          ? kitData.sequence.map(Boolean)
          : [];

        /*
        Load exactly according to the saved pattern size.

        A 16-step saved pattern becomes 16 steps.
        A 32-step saved pattern becomes 32 steps.
      */
        return Array(targetStepCount)
          .fill(false)
          .map((_, stepIndex) => Boolean(savedSequence[stepIndex]));
      });

      setSequences(newSequences);
      setKitVolumes(newKitVolumes);

      const loadedInstrumentGrids = {};

      const loadedInstruments = await Promise.all(
        parsedInstruments.map(async (instrument) => {
          if (!instrument?.InsName) {
            return null;
          }

          await InstrumentAudioContext.loadAudio(instrument.InsName);

          const instrumentVolume = Number(
            instrument.InsVolume ?? instrument.volume ?? 0.5,
          );

          InstrumentAudioContext.setInstVolume(
            instrumentVolume,
            instrument.InsName,
          );

          const loadedGrid = await fetchNotesAndSetGrid(
            instrument,
            instrument.PianoRollPatternNo,
            targetStepCount,
          );

          loadedInstrumentGrids[instrument.InsName] = loadedGrid;

          return {
            ...instrument,
            InsVolume: instrumentVolume,
          };
        }),
      );

      const validLoadedInstruments = loadedInstruments.filter(Boolean);

      setSelectedInstruments(validLoadedInstruments);
      setGridState(loadedInstrumentGrids);
      setVirtualGrids(loadedInstrumentGrids);

      /*
      Keep the open Melody Modal attached to the corresponding
      instrument row.
    */
      if (modalInstrumentIndex !== null) {
        const replacementInstrument =
          validLoadedInstruments[modalInstrumentIndex];

        if (replacementInstrument) {
          setModalInstrument(replacementInstrument.InsName);

          setMelodyInstrumentPatternNo(
            replacementInstrument.PianoRollPatternNo || "",
          );
        } else {
          setModalInstrument(null);
          setModalInstrumentIndex(null);
          setMelodyInstrumentPatternNo("");
        }
      }

      await loadAudioForUpdatedPatterns();
    } catch (error) {
      console.error("Failed to retrieve pattern:", error);
      toast.error("Failed to load the selected pattern.");
    }
  };

  const handleClearPattern = () => {
    addToHistory(pads.map(() => Array(stepCount).fill(false)));

    setSelectedGenre("");
    setSelectedPattern("initial");

    setSelectedInstruments([]);
    setVirtualGrids({});
    setGridState({});
    setLoadedMelodyData({});
    setGlobalActiveNotesByInstrument({});

    const resetVolumes = {};
    pads.forEach((pad) => {
      resetVolumes[pad.kitUrl] = 0.5;
      InstrumentAudioContext.setKitVolume(0.5, pad.kitUrl);
    });

    setKitVolumes(resetVolumes);
    InstrumentAudioContext.stopAllNotes();
  };
  useEffect(() => {
    const loadPatternFromSongEdit = async () => {
      if (!editSongPatternNumber || !user || beatPatterns.length === 0) return;

      const matchedPattern = beatPatterns.find(
        (pattern) =>
          String(pattern.patternNumber) === String(editSongPatternNumber),
      );

      if (!matchedPattern) return;

      await handleUserPatternChange({
        target: { value: matchedPattern.$id },
      });

      setEditSongPatternNumber(null);
    };

    loadPatternFromSongEdit();
  }, [editSongPatternNumber, user, beatPatterns]);
  const buildGridFromMelodyData = (data) => {
    if (data.grid) {
      return data.grid;
    }
    const newGrid = Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false));

    if (data.type === "chords") {
      data.chordNotes.forEach(({ note, step }) => {
        const rowIndex = notes.findIndex((n) => n === note);
        if (rowIndex !== -1 && step >= 0 && step < stepCount) {
          newGrid[step][rowIndex] = true;
        }
      });
    } else if (data.notes?.length) {
      data.notes.forEach((noteName, colIndex) => {
        const rowIndex = notes.findIndex((n) => n === noteName);
        if (rowIndex !== -1 && colIndex < stepCount) {
          newGrid[colIndex][rowIndex] = true;
        }
      });
    }

    return newGrid;
  };
  const getAutoStepCount = (length) => {
    if (length <= 16) return 16;
    if (length <= 32) return 32;
    if (length <= 64) return 64;
    return length;
  };
  const detectStepCountFromKits = (parsedKits) => {
    const maxLength = Math.max(
      16,
      ...Object.values(parsedKits || {}).map(
        (kit) => kit.sequence?.length || 16,
      ),
    );

    return getAutoStepCount(maxLength);
  };
  const handleLoadMelodyToPianoRoll = (data) => {
    const name = data.ragaName || data.name || "Loaded Melody";
    const tempPatternNo = data.patternNo || `melody-${Date.now()}`;
    const grid = buildGridFromMelodyData(data);
    const savedInstrumentName = data.instrumentName || modalInstrument;

    setLoadedMelodyData((prev) => ({
      ...prev,
      [modalInstrument]: { ...data, name, patternNo: tempPatternNo, grid },
    }));
    setVirtualGrids((prev) => ({
      ...prev,
      [modalInstrument]: grid,
    }));

    // only Save should change dropdown / permanent instrument pattern
    if (data.type === "saved") {
      setGridState((prev) => ({
        ...prev,
        [savedInstrumentName]: grid,
      }));

      setSelectedInstruments((prev) =>
        prev.map((inst) =>
          inst.InsName === modalInstrument
            ? {
                ...inst,
                InsName: savedInstrumentName,
                PianoRollPatternNo: tempPatternNo,
                PianoRollPatternName: "",
              }
            : inst,
        ),
      );
      setVirtualGrids((prev) => {
        const updated = { ...prev };
        delete updated[modalInstrument];
        updated[savedInstrumentName] = grid;
        return updated;
      });
    }
  };
  useEffect(() => {
    stepsRefs.current.forEach((ref, index) => {
      if (ref.current) {
        ref.current.addEventListener("scroll", () => handleScroll(index));
      }
    });

    return () => {
      stepsRefs.current.forEach((ref) => {
        if (ref.current) {
          ref.current.removeEventListener("scroll", handleScroll);
        }
      });
    };
  }, []);
  const handleMelodyPatternSelectChange = async ({
    instrumentName,
    patternNo,
    grid,
    deletedPatternNo,
  }) => {
    setSelectedInstruments((previousInstruments) =>
      previousInstruments.map((instrument) => {
        const belongsToInstrument =
          instrument.InsName === modalInstrument ||
          instrument.InsName === instrumentName;

        if (!belongsToInstrument) {
          return instrument;
        }

        // Only clear it when this instrument was using the deleted preset.
        if (deletedPatternNo) {
          const wasUsingDeletedPattern =
            String(instrument.PianoRollPatternNo) === String(deletedPatternNo);

          if (!wasUsingDeletedPattern) {
            return instrument;
          }
        }

        return {
          ...instrument,
          PianoRollPatternNo: patternNo || "",
          PianoRollPatternName: "",
        };
      }),
    );

    const targetInstrument = instrumentName || modalInstrument;

    if (grid) {
      setGridState((previous) => ({
        ...previous,
        [targetInstrument]: grid,
      }));

      setVirtualGrids((previous) => ({
        ...previous,
        [targetInstrument]: grid,
      }));
    } else {
      const emptyGrid = makeEmptyGrid();

      setGridState((previous) => ({
        ...previous,
        [targetInstrument]: emptyGrid,
      }));

      setVirtualGrids((previous) => ({
        ...previous,
        [targetInstrument]: emptyGrid,
      }));
    }

    // Refresh the preset dropdown list after deletion.
    if (deletedPatternNo) {
      const refreshedPatterns = await fetchPianoRollPatterns();
      setPianoRollPatterns(refreshedPatterns);
    }
  };
  const handlePianoRollPatternChange = async (instrumentName, patternId) => {
    const isTempMelody =
      String(patternId).startsWith("melody-") ||
      String(patternId).startsWith("loaded-");

    setSelectedInstruments((prev) =>
      prev.map((inst) =>
        inst.InsName === instrumentName
          ? { ...inst, PianoRollPatternNo: patternId }
          : inst,
      ),
    );

    if (isTempMelody) {
      const loaded = loadedMelodyData[instrumentName];

      if (loaded?.grid) {
        setGridState((prevGrid) => ({
          ...prevGrid,
          [instrumentName]: loaded.grid,
        }));

        setVirtualGrids((prevGrids) => ({
          ...prevGrids,
          [instrumentName]: loaded.grid,
        }));
      }

      return;
    }

    const notesArray = await fetchNotesPianoRoll(patternId);

    const newGrid = buildRepeatingGridFromNotes(notesArray, stepCount);

    setGridState((previous) => ({
      ...previous,
      [instrumentName]: newGrid,
    }));

    setVirtualGrids((previous) => ({
      ...previous,
      [instrumentName]: newGrid,
    }));

    await fetchBeatPatterns();
    loadAudioForUpdatedPatterns();
  };

  // New useEffect hook to watch for changes in selectedPattern and selectedInstruments
  useEffect(() => {
    if (selectedPattern && selectedPattern !== "initial") {
      const fetchAndSetVirtualGrids = async () => {
        const newVirtualGrids = {};
        for (const instrument of selectedInstruments) {
          const patternNo = String(instrument.PianoRollPatternNo || "");

          if (
            patternNo.startsWith("melody-") ||
            patternNo.startsWith("loaded-")
          ) {
            if (gridState[instrument.InsName]) {
              newVirtualGrids[instrument.InsName] =
                gridState[instrument.InsName];
            }
            continue;
          }

          const notesArray = await fetchNotesPianoRoll(
            instrument.PianoRollPatternNo,
          );
          const newGrid = Array(stepCount)
            .fill()
            .map(() => Array(notes.length).fill(false));
          notesArray.forEach((note) => {
            const [noteName, bar] = note.split(",");
            const rowIndex = notes.findIndex((n) => n === noteName);
            const colIndex = parseInt(bar, 10) - 1;
            if (rowIndex !== -1 && colIndex >= 0 && colIndex < stepCount) {
              newGrid[colIndex][rowIndex] = true;
            }
          });
          newVirtualGrids[instrument.InsName] = newGrid;
        }
        setVirtualGrids(newVirtualGrids);
      };
      fetchAndSetVirtualGrids();
    }
  }, [selectedPattern, selectedInstruments]);

  const handleScroll = (idx) => {
    const scrollPosition = stepsRefs.current[idx].current.scrollLeft;
    stepsRefs.current.forEach((ref, index) => {
      if (index !== idx && ref.current) {
        ref.current.scrollLeft = scrollPosition;
      }
    });
  };
  useEffect(() => {
    if (selectedPattern && selectedPattern !== "initial") {
      const fetchAndSetVirtualGrids = async () => {
        const newVirtualGrids = {};
        for (const instrument of selectedInstruments) {
          const patternNo = String(instrument.PianoRollPatternNo || "");

          if (
            patternNo.startsWith("melody-") ||
            patternNo.startsWith("loaded-")
          ) {
            if (gridState[instrument.InsName]) {
              newVirtualGrids[instrument.InsName] =
                gridState[instrument.InsName];
            }
            continue;
          }

          const notesArray = await fetchNotesPianoRoll(
            instrument.PianoRollPatternNo,
          );
          const newGrid = Array(stepCount)
            .fill()
            .map(() => Array(notes.length).fill(false));
          notesArray.forEach((note) => {
            const [noteName, bar] = note.split(",");
            const rowIndex = notes.findIndex((n) => n === noteName);
            const colIndex = parseInt(bar, 10) - 1;
            if (rowIndex !== -1 && colIndex >= 0 && colIndex < stepCount) {
              newGrid[colIndex][rowIndex] = true;
            }
          });
          newVirtualGrids[instrument.InsName] = newGrid;
        }
        setVirtualGrids(newVirtualGrids);
      };
      fetchAndSetVirtualGrids();
    }
  }, [selectedPattern, selectedInstruments]);
  useEffect(() => {
    if (isPlaying || isGlobalPlaying) {
      const interval = (60 / bpm) * 250;
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(
        () => {
          setCurrentStep((prevStep) => {
            const nextStep = (prevStep + 1) % stepCount;
            onStepChange?.(nextStep);
            // Trigger drum sounds
            const swingDelay = getSwingDelay(nextStep);

            sequences.forEach((sequence, padIndex) => {
              if (sequence[nextStep]) {
                setTimeout(() => {
                  InstrumentAudioContext.playSound(pads[padIndex].kitUrl);
                }, swingDelay);
              }
            });

            setTimeout(() => {
              playAllInstrumentsAtCurrentBar(nextStep);
            }, swingDelay);

            return nextStep;
          });
        },
        interval,
        stepCount,
      );
    } else {
      clearInterval(intervalRef.current);
      InstrumentAudioContext.stopAllNotes();
    }
    return () => clearInterval(intervalRef.current);
  }, [
    isPlaying,
    isGlobalPlaying,
    bpm,
    sequences,
    selectedInstruments,
    virtualGrids,
    previewInstrumentByOriginal,
    isSwingOn,
  ]);

  const increaseBpm = () => setBpm((bpm) => (bpm + 5 > 300 ? 300 : bpm + 5));
  const decreaseBpm = () => setBpm((bpm) => (bpm - 5 < 20 ? 20 : bpm - 5));

  const genreKeys = Object.keys(genrePatterns);
  const cycleGenre = (direction) => {
    if (trialDropdownLocked) {
      openPlansModal();
      return;
    }

    if (!genreKeys.length) return;

    const currentIndex = genreKeys.indexOf(selectedGenre);
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;

    const nextIndex =
      direction === "next"
        ? (safeIndex + 1) % genreKeys.length
        : (safeIndex - 1 + genreKeys.length) % genreKeys.length;

    const nextGenre = genreKeys[nextIndex];

    handleGenreChange({
      target: {
        value: nextGenre,
      },
    });
  };
  const handleGenreChange = (event) => {
    if (trialDropdownLocked) {
      openPlansModal();
      return;
    }

    const genre = event.target.value;
    setSelectedGenre(genre);

    if (!genre) {
      setSequences(pads.map(() => Array(stepCount).fill(false)));
      return;
    }

    const selectedPatternData = genrePatterns[genre];

    const newSequences = pads.map((pad) => {
      const kitName = pad.kitName?.toLowerCase().replace(/\s+/g, "");

      const basePattern = selectedPatternData[kitName]
        ? selectedPatternData[kitName].map((beat) => beat === 1)
        : Array(16).fill(false);

      return Array(stepCount)
        .fill(false)
        .map((_, i) => basePattern[i % basePattern.length]);
    });

    addToHistory(newSequences);
    setSequences(newSequences);
  };
  const handleSavePattern = async () => {
    const newPatternId = await saveBeatPattern(
      sequences,
      pads,
      selectedPattern,
      beatPatterns,
      setBeatPatterns,
      fetchBeatPatterns,
      user,
      setIsSaving,
      selectedInstruments, // Pass selectedInstruments
      instrumentVolumes, // Pass instrumentVolumes
      stepCount,
    );
    if (newPatternId) {
      setSelectedPattern(newPatternId);
    }
  };

  const handleDeletePattern = () => {
    deleteBeatPattern(
      selectedPattern,
      beatPatterns,
      setBeatPatterns,
      setSelectedPattern,
      setSequences,
      pads,
      user,
      setIsDeleting,
      stepCount,
    );
  };

  const handleKitVolumeChange = (kitId) => (e) => {
    const volume = parseFloat(e.target.value);
    setKitVolumes((prevVolumes) => ({
      ...prevVolumes,
      [kitId]: volume,
    }));
    InstrumentAudioContext.setKitVolume(volume, kitId);
  };

  const handleInstrumentChange = async (event) => {
    const instrument = event.target.value;
    setSelectedInstrument(instrument);
    if (instrument) {
      await InstrumentAudioContext.loadAudio(instrument);
    }
  };

  const addInstrument = async () => {
    if (
      selectedInstrument &&
      !selectedInstruments.some((inst) => inst.InsName === selectedInstrument)
    ) {
      await InstrumentAudioContext.loadAudio(selectedInstrument);
      setSelectedInstruments([
        { InsName: selectedInstrument, PianoRollPatternNo: "", InsVolume: 0.5 },
        ...selectedInstruments,
      ]);
      InstrumentAudioContext.setInstVolume(0.5, selectedInstrument); // Set initial volume
      setInstrumentVolumes((prevVolumes) => ({
        ...prevVolumes,
        [selectedInstrument]: 0.5,
      }));
    }
  };

  const debouncedSetVolume = useCallback(
    debounce((volume, instrument) => {
      InstrumentAudioContext.setInstVolume(volume, instrument);
      setIsVolumeChanging(false);
    }, 100),
    [],
  );

  const handleVolumeChange = (instrument) => (e) => {
    const volume = parseFloat(e.target.value);
    setInstrumentVolumes((prevVolumes) => ({
      ...prevVolumes,
      [instrument]: volume,
    }));
    setIsVolumeChanging(true);
    debouncedSetVolume(volume, instrument);
  };

  const removeInstrument = (instrument) => {
    toast(
      ({ closeToast }) => (
        <div
          style={{
            background:
              "linear-gradient(145deg, #177b5dff, #13134aff, #175d7bff)",
            color: "#fff",
            border: "1px solid #6d3cff",
            borderRadius: 12,
            padding: "14px",
            boxShadow:
              "0 0 12px rgba(255, 255, 255, 0.6), inset 0 0 10px rgba(0,255,255,.08)",
            minWidth: 220,
            textAlign: "center",
          }}
        >
          <div
            style={{
              marginBottom: 14,
              color: "#d7c8ff",
              fontSize: 15,
              textShadow: "0 0 8px #6d3cff",
            }}
          >
            Delete <b style={{ color: "#00f7ff" }}>{instrument}</b>?
          </div>

          <button
            onClick={() => {
              setSelectedInstruments((prev) =>
                prev.filter((inst) => inst.InsName !== instrument),
              );
              closeToast();
            }}
            style={{
              marginRight: 10,
              padding: "6px 16px",
              borderRadius: 8,
              border: "1px solid #00ff88",
              background: "#0b1b14",
              color: "#00ff88",
              cursor: "pointer",
              boxShadow: "0 0 10px rgba(0,255,136,.6)",
              transition: "all .25s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#00ff88";
              e.currentTarget.style.color = "#000";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#0b1b14";
              e.currentTarget.style.color = "#00ff88";
            }}
          >
            ✔ Yes
          </button>

          <button
            onClick={closeToast}
            style={{
              padding: "6px 16px",
              borderRadius: 8,
              border: "1px solid #ff4d8d",
              background: "#220912",
              color: "#ff6aa9",
              cursor: "pointer",
              boxShadow: "0 0 10px rgba(255,77,141,.55)",
              transition: "all .25s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#ff4d8d";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#220912";
              e.currentTarget.style.color = "#ff6aa9";
            }}
          >
            ✖ No
          </button>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
        className: "delete-confirm-toast",
        bodyClassName: "delete-confirm-toast-body",
      },
    );
  };
  const openMelodyModal = (instrument, instrumentIndex) => {
    // Clicking the keyboard button of the currently open row closes it.
    if (modalInstrumentIndex === instrumentIndex) {
      closeMelodyModal();
      return;
    }

    const selectedInstrument = selectedInstruments[instrumentIndex];

    if (!selectedInstrument) return;

    setModalInstrumentIndex(instrumentIndex);
    setModalInstrument(selectedInstrument.InsName);

    setMelodyInstrumentPatternNo(selectedInstrument.PianoRollPatternNo || "");
  };

  const closeMelodyModal = async (newPatternNo, newInstrumentName) => {
    const closingInstrumentIndex = modalInstrumentIndex;
    setModalInstrument(null);

    const pianoRollPatterns = await fetchPianoRollPatterns();
    setPianoRollPatterns(pianoRollPatterns);

    setSelectedInstruments((prevInstruments) =>
      prevInstruments.map((inst) =>
        inst.InsName === modalInstrument
          ? {
              ...inst,
              InsName: newInstrumentName || inst.InsName,
              PianoRollPatternNo: newPatternNo || inst.PianoRollPatternNo,
            }
          : inst,
      ),
    );

    if (newInstrumentName && newInstrumentName !== modalInstrument) {
      await InstrumentAudioContext.loadAudio(newInstrumentName);
    }

    await fetchBeatPatterns();
    loadAudioForUpdatedPatterns();
  };

  const openPianoRollModal = (instrument) => {
    const selectedInstrument = selectedInstruments.find(
      (inst) => inst.InsName === instrument,
    );
    const patternNo = selectedInstrument
      ? selectedInstrument.PianoRollPatternNo
      : null;
    setPianoRollInstrument(instrument); // Only set the instrument name
    setPianoRollPatternNo(patternNo); // Separate state for the pattern number
  };

  const closePianoRollModal = async (updatedGrid, newPatternNo) => {
    setPianoRollInstrument(null);
    if (updatedGrid) {
      setGridState((prevGrid) => ({
        ...prevGrid,
        [pianoRollInstrument]: updatedGrid,
      }));

      setVirtualGrids((prevGrid) => ({
        ...prevGrid,
        [pianoRollInstrument]: updatedGrid,
      }));

      setLoadedMelodyData((prev) => ({
        ...prev,
        [pianoRollInstrument]: prev[pianoRollInstrument]
          ? {
              ...prev[pianoRollInstrument],
              grid: updatedGrid,
            }
          : prev[pianoRollInstrument],
      }));
    }

    // Fetch and set the new patterns
    const pianoRollPatterns = await fetchPianoRollPatterns();
    setPianoRollPatterns(pianoRollPatterns);

    if (newPatternNo) {
      const updatedInstruments = selectedInstruments.map((inst) =>
        inst.InsName === pianoRollInstrument
          ? { ...inst, PianoRollPatternNo: newPatternNo }
          : inst,
      );
      setSelectedInstruments(updatedInstruments);
    }

    // Fetch the latest beat patterns
    await fetchBeatPatterns();
    // Load audio for updated beat patterns
    loadAudioForUpdatedPatterns();
  };
  const handlePianoRollLivePreview = (instrumentName, liveGrid) => {
    setVirtualGrids((prev) => ({
      ...prev,
      [instrumentName]: liveGrid,
    }));
  };
  const loadAudioForUpdatedPatterns = async () => {
    for (const instrument of selectedInstruments) {
      await InstrumentAudioContext.loadAudio(instrument.InsName);
    }
  };
  const handleGlobalPlayPause = () => {
    setIsGlobalPlaying((prev) => {
      const newIsGlobalPlaying = !prev;
      onGlobalPlayChange?.(newIsGlobalPlaying);
      if (newIsGlobalPlaying) {
        // Global playback takes full control.
        InstrumentAudioContext.stopAllNotes();

        // The playback interval will advance -1 to step 0.
        setCurrentStep(-1);
        setCurrentBar(0);
        setGlobalActiveNotesByInstrument({});

        // Ensure virtualGrids are set for all instruments
        const newVirtualGrids = {};
        selectedInstruments.forEach((instrument) => {
          const grid = gridState[instrument.InsName];
          if (grid) {
            newVirtualGrids[instrument.InsName] = grid;
          }
        });
        setVirtualGrids(newVirtualGrids);
        loadAudioForUpdatedPatterns(); // Load audio for all instruments
      } else {
        InstrumentAudioContext.stopAllNotes();
      }
      return newIsGlobalPlaying;
    });
  };

  useEffect(() => {
    if (isGlobalPlaying) {
      const interval = (60 / bpm) * 250; // Adjust interval calculation to ensure correct timing
      globalIntervalRef.current = setInterval(() => {
        setCurrentBar((prevBar) => (prevBar + 1) % stepCount);
      }, interval);
    } else {
      clearInterval(globalIntervalRef.current);
      InstrumentAudioContext.stopAllNotes();
    }

    return () => clearInterval(globalIntervalRef.current);
  }, [isGlobalPlaying, bpm]);

  const playAllInstrumentsAtCurrentBar = (bar) => {
    const nextActiveNotes = {};

    selectedInstruments.forEach((instrument) => {
      const grid = virtualGrids[instrument.InsName] || [];
      const notesForInstrument = [];

      if (grid[bar]) {
        grid[bar].forEach((active, noteIndex) => {
          if (active) {
            const note = notes[noteIndex];
            notesForInstrument.push(note);
            const playbackInstrument =
              previewInstrumentByOriginal[instrument.InsName] ||
              instrument.InsName;
            InstrumentAudioContext.playPianoRollNote(
              noteIndex,
              notes,
              playbackInstrument,
              900,
            );
          }
        });
      }

      nextActiveNotes[instrument.InsName] = notesForInstrument;
    });

    setGlobalActiveNotesByInstrument(nextActiveNotes);
  };

  const handleGlobalStop = () => {
    setIsGlobalPlaying(false);
    setCurrentBar(0);
    setCurrentStep(-1);
    setGlobalActiveNotesByInstrument({});
    InstrumentAudioContext.stopAllNotes();
  };
  const handleKitMuteToggle = (kitId) => {
    setKitMuteStates((prevStates) => {
      const newState = !prevStates[kitId];
      InstrumentAudioContext.setMute(newState, kitId);
      return { ...prevStates, [kitId]: newState };
    });
  };

  const handleInstrumentMuteToggle = (instrumentName) => {
    setInstrumentMuteStates((prevStates) => {
      const newState = !prevStates[instrumentName];
      InstrumentAudioContext.setMute(newState, instrumentName);
      return { ...prevStates, [instrumentName]: newState };
    });
  };
  const handleToggleAllKitsMute = () => {
    setKitMuteStates((prevStates) => {
      const newStates = {};
      const newStateValue = !Object.values(prevStates).every(Boolean);
      pads.forEach((pad) => {
        InstrumentAudioContext.setMute(newStateValue, pad.kitUrl);
        newStates[pad.kitUrl] = newStateValue;
      });
      return newStates;
    });
  };

  const handleToggleAllInstrumentsMute = () => {
    setInstrumentMuteStates((prevStates) => {
      const newStates = {};
      const newStateValue = !Object.values(prevStates).every(Boolean);
      selectedInstruments.forEach((instrument) => {
        InstrumentAudioContext.setMute(newStateValue, instrument.InsName);
        newStates[instrument.InsName] = newStateValue;
      });
      return newStates;
    });
  };
  const addToHistory = (newSequences) => {
    setHistory((prevHistory) => [...prevHistory, sequences]);
    setRedoStack([]);
    setSequences(newSequences);
  };

  const undo = () => {
    if (history.length > 0) {
      const previousSequences = history[history.length - 1];
      setHistory((prevHistory) => prevHistory.slice(0, -1));
      setRedoStack((prevRedoStack) => [sequences, ...prevRedoStack]);
      setSequences(previousSequences);
    }
  };

  const redo = () => {
    if (redoStack.length > 0) {
      const nextSequences = redoStack[0];
      setRedoStack((prevRedoStack) => prevRedoStack.slice(1));
      setHistory((prevHistory) => [...prevHistory, sequences]);
      setSequences(nextSequences);
    }
  };
  const cycleInstrument = (currentInstrument, direction, instrumentIndex) => {
    const currentIndex = instruments.indexOf(currentInstrument);
    if (currentIndex === -1) return;

    const nextIndex =
      direction === "next"
        ? (currentIndex + 1) % instruments.length
        : (currentIndex - 1 + instruments.length) % instruments.length;

    handleInstrumentChangeFromDropdown(
      currentInstrument,
      instruments[nextIndex],
      instrumentIndex,
    );
  };

  const cyclePreset = (instrumentName, currentPatternNo, direction) => {
    if (!pianoRollPatterns.length) return;

    const patternNos = pianoRollPatterns.map((p) => String(p.patternNo));

    const currentIndex = patternNos.indexOf(String(currentPatternNo));
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;

    const nextIndex =
      direction === "next"
        ? (safeIndex + 1) % patternNos.length
        : (safeIndex - 1 + patternNos.length) % patternNos.length;

    handlePianoRollPatternChange(instrumentName, patternNos[nextIndex]);
  };
  const cycleSelectedInstrument = (direction) => {
    if (!instruments.length) return;

    const currentIndex = instruments.indexOf(selectedInstrument);

    const safeIndex = currentIndex === -1 ? 0 : currentIndex;

    const nextIndex =
      direction === "next"
        ? (safeIndex + 1) % instruments.length
        : (safeIndex - 1 + instruments.length) % instruments.length;

    const nextInstrument = instruments[nextIndex];

    setSelectedInstrument(nextInstrument);

    // Call the same logic as selecting from the dropdown
    handleInstrumentChange({
      target: {
        value: nextInstrument,
      },
    });
  };
  return (
    <div className="sequencer">
      <div className="controls-seq merged-controls">
        <div className="playback-controls-ins">
          <button onClick={handleGlobalPlayPause} className="play-button-seq">
            {isGlobalPlaying ? <FaPause /> : <FaPlay />}
          </button>
          <button onClick={handleGlobalStop} className="stop-button">
            <FaStop />
          </button>{" "}
        </div>
        <div className="bpm-controls">
          <button onClick={decreaseBpm} className="bpm-button">
            <FaMinus />
          </button>
          <span className="bpm-display">{bpm} BPM</span>
          <button onClick={increaseBpm} className="bpm-button">
            <FaPlus />
          </button>
        </div>
        <button className="add-sound" onClick={onAddKit}>
          Add kit
        </button>
        <button
          className="record-button4"
          onClick={() => setIsRecording((prev) => !prev)}
        >
          <FaCircle color={isRecording ? "green" : "black"} />
          <p>REC</p>
        </button>
        <button onClick={handleMapButtonClick} className="map-button">
          Map
        </button>{" "}
        <button
          className={`overwrite-btn ${overwriteMode ? "active" : ""}`}
          onClick={() => setOverwriteMode((prev) => !prev)}
          title="Overwrite recording"
        >
          ✎
        </button>
        <button
          onClick={handleClockButtonClick}
          className="save-pattern-button"
        >
          <FaClock />
        </button>{" "}
        <button
          onClick={() => setIsSwingOn((prev) => !prev)}
          className={`swing-button ${isSwingOn ? "active" : ""}`}
        >
          SWING
        </button>{" "}
        <button
          onClick={toggleStepCount}
          className="step-count"
          title={`Switch to ${stepCount === 16 ? 32 : 16} steps`}
        >
          {stepCount === 16 ? "16" : "32"}
        </button>{" "}
        <div className="octave-controls">
          {" "}
          <select
            className="instrument-dropdown"
            onChange={handleUserPatternChange}
            value={selectedPattern}
          >
            <option value="initial">Select Pattern</option>
            {beatPatterns.map((pattern) => (
              <option key={pattern.$id} value={pattern.$id}>
                Pattern {pattern.patternNumber}
              </option>
            ))}
          </select>
          <button
            onClick={handleSavePattern}
            className="save-pattern-button"
            style={{ cursor: isSaving ? "wait" : "pointer" }}
            disabled={isSaving}
          >
            {isSaving ? <ClipLoader size={20} color="#fff" /> : <FaSave />}
          </button>
          <button
            onClick={handleDeletePattern}
            className="delete-pattern-button"
          >
            {isDeleting ? <ClipLoader size={20} color="#fff" /> : <FaTrash />}
          </button>
        </div>
        <button
          onClick={handleClearPattern}
          className="clear-pattern-button"
          title="Clear pattern"
        >
          X
        </button>{" "}
      </div>

      <div className="drum-header1">
        <h3>DRUMS</h3>{" "}
        <div className="octave-controls">
          <select
            className="roll-pattern-dropdown"
            onChange={handleGenreChange}
            value={selectedGenre}
            disabled={trialDropdownLocked}
          >
            <option value="">Genre ~</option>
            {genreKeys.map((genre) => (
              <option key={genre} value={genre}>
                {genre}
              </option>
            ))}
          </select>{" "}
          <button
            className="preset-nav-btn"
            onClick={() => cycleGenre("prev")}
            disabled={trialDropdownLocked}
          >
            &lt;
          </button>
          <button
            className="preset-nav-btn"
            onClick={() => cycleGenre("next")}
            disabled={trialDropdownLocked}
          >
            &gt;
          </button>
        </div>{" "}
        <div className="bpm-controls compact-bpm-controls">
          <button onClick={undo} className="undo-button">
            <FaUndo />
          </button>

          <button onClick={redo} className="redo-button">
            <FaRedo />
          </button>
          <button onClick={handleToggleAllKitsMute} className="mute-button">
            {Object.values(kitMuteStates).every(Boolean) ? (
              <FaVolumeMute className="mute" />
            ) : (
              <FaVolumeUp className="mute-no" />
            )}
          </button>
        </div>
      </div>

      {pads.map((pad, index) => (
        <div key={pad.$id} className="track1">
          <button
            className={`kit-name ${kitButtonActive === index ? "active" : ""}`}
            onClick={() => handleKitButtonClick(index)}
          >
            {pad.kitName || "Unnamed Kit"}
          </button>
          <div className="steps" ref={stepsRefs.current[index]}>
            {sequences[index].map((active, beatIndex) => (
              <button
                key={beatIndex}
                className={`beat ${active ? "active" : ""} ${
                  currentStep === beatIndex ? "current" : ""
                }`}
                onClick={() => toggleBeat(index, beatIndex)}
              />
            ))}
          </div>
          <div className="kit-volume-control">
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={kitVolumes[pad.kitUrl] ?? 0.5} // Ensure default value is provided
              onChange={handleKitVolumeChange(pad.kitUrl)}
              className="volume-slider-seq"
            />
            <button
              onClick={() => handleKitMuteToggle(pad.kitUrl)}
              className="mute-button"
            >
              {kitMuteStates[pad.kitUrl] ? (
                <FaVolumeMute className="mute" />
              ) : (
                <FaVolumeUp className="mute-no" />
              )}
            </button>
          </div>
        </div>
      ))}

      <br />
      <div className="drum-header1">
        <h3>INSTRUMENTS</h3>{" "}
        <div className="octave-controls">
          {" "}
          <select
            className="instrument-dropdown"
            value={selectedInstrument}
            onChange={handleInstrumentChange}
          >
            <option value="">Select</option>
            {instruments.map((instrument) => (
              <option key={instrument} value={instrument}>
                {instrument}
              </option>
            ))}
          </select>{" "}
          <button
            className="preset-nav-btn"
            onClick={() => cycleSelectedInstrument("prev")}
          >
            &lt;
          </button>
          <button
            className="preset-nav-btn"
            onClick={() => cycleSelectedInstrument("next")}
          >
            &gt;
          </button>
        </div>{" "}
        <button onClick={addInstrument} className="add-instrument-button">
          <FaPlus />
        </button>{" "}
        <button
          onClick={handleToggleAllInstrumentsMute}
          className="mute-button"
        >
          {Object.values(instrumentMuteStates).every(Boolean) ? (
            <FaVolumeMute className="mute" />
          ) : (
            <FaVolumeUp className="mute-no" />
          )}
        </button>
      </div>

      <div className="selected-instruments">
        {selectedInstruments.map((instrument, instrumentIndex) => (
          <div key={instrument.InsName} className="selected-instrument">
            <div className="octave-controls">
              <select
                className="instrument-dropdown"
                onChange={(e) =>
                  handleInstrumentChangeFromDropdown(
                    instrument.InsName,
                    e.target.value,
                    instrumentIndex,
                  )
                }
                value={instrument.InsName}
              >
                {instruments.map((inst) => (
                  <option key={inst} value={inst}>
                    {inst}
                  </option>
                ))}
              </select>{" "}
              <button
                className="preset-nav-btn"
                onClick={() =>
                  cycleInstrument(instrument.InsName, "prev", instrumentIndex)
                }
              >
                &lt;
              </button>{" "}
              <button
                className="preset-nav-btn"
                onClick={() =>
                  cycleInstrument(instrument.InsName, "next", instrumentIndex)
                }
              >
                &gt;
              </button>
            </div>
            <div className="make-melody-button-container">
              <button
                className="make-melody-button"
                onClick={() =>
                  openMelodyModal(instrument.InsName, instrumentIndex)
                }
              >
                🎹
              </button>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={instrumentVolumes[instrument.InsName] ?? 0.5} // Ensure default value is provided
              onChange={handleVolumeChange(instrument.InsName)}
              className="volume-slider-seq"
            />{" "}
            <button
              onClick={() => handleInstrumentMuteToggle(instrument.InsName)}
              className="mute-button"
            >
              {instrumentMuteStates[instrument.InsName] ? (
                <FaVolumeMute className="mute" />
              ) : (
                <FaVolumeUp className="mute-no" />
              )}
            </button>
            <div className="octave-controls">
              <select
                className="roll-pattern-dropdown"
                value={instrument.PianoRollPatternNo || ""}
                onChange={(e) =>
                  handlePianoRollPatternChange(
                    instrument.InsName,
                    e.target.value,
                  )
                }
              >
                <option value="">Preset ~</option>
                {instrument.PianoRollPatternName && (
                  <option value={instrument.PianoRollPatternNo}>
                    {instrument.PianoRollPatternName}
                  </option>
                )}

                {pianoRollPatterns.map((pattern) => (
                  <option key={pattern.$id} value={pattern.patternNo}>
                    Preset: {pattern.patternNo}
                  </option>
                ))}
              </select>
              <button
                className="preset-nav-btn"
                onClick={() =>
                  cyclePreset(
                    instrument.InsName,
                    instrument.PianoRollPatternNo,
                    "prev",
                  )
                }
              >
                &lt;
              </button>{" "}
              <button
                className="preset-nav-btn"
                onClick={() =>
                  cyclePreset(
                    instrument.InsName,
                    instrument.PianoRollPatternNo,
                    "next",
                  )
                }
              >
                &gt;
              </button>{" "}
            </div>
            <button
              className="piano-roll-button-notes"
              onClick={() => openPianoRollModal(instrument.InsName)}
            >
              Notes 🎵
            </button>
            <div className="remove-button-container">
              <button
                onClick={() => removeInstrument(instrument.InsName)}
                className="remove-instrument-button"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        ))}
      </div>

      {modalInstrument && (
        <MelodyModal
          instrument={modalInstrument}
          onClose={closeMelodyModal}
          bpm={bpm}
          setBpm={setBpm}
          selectedInstruments={selectedInstruments}
          setSelectedInstruments={setSelectedInstruments}
          initialPatternNo={melodyInstrumentPatternNo}
          onLoadRagaToPianoRoll={handleLoadMelodyToPianoRoll}
          onPreviewInstrumentChange={handleMelodyInstrumentPreview}
          onPatternSelectChange={handleMelodyPatternSelectChange}
          globalPlayingNotes={
            globalActiveNotesByInstrument[modalInstrument] || []
          }
          onInstrumentChange={handleMelodyInstrumentChange}
          isGlobalPlaying={isGlobalPlaying}
          trialDropdownLocked={trialDropdownLocked}
          openPlansModal={openPlansModal}
          trialSeconds={trialSeconds}
          isTrial={isTrial}
          stepCount={stepCount}
          isSwingOn={isSwingOn}
          swingAmount={swingAmount}
        />
      )}
      {pianoRollInstrument && (
        <PianoRollModal
          instrument={pianoRollInstrument}
          initialPatternNo={
            String(pianoRollPatternNo || "").startsWith("melody-") ||
            String(pianoRollPatternNo || "").startsWith("loaded-")
              ? ""
              : pianoRollPatternNo
          }
          initialRagaGrid={loadedMelodyData[pianoRollInstrument]}
          onClose={closePianoRollModal}
          bpm={bpm}
          setBpm={setBpm}
          selectedInstruments={selectedInstruments}
          setSelectedInstruments={setSelectedInstruments}
          globalPlayingNotes={
            globalActiveNotesByInstrument[pianoRollInstrument] || []
          }
          globalCurrentBar={currentStep}
          onLivePreviewGridChange={handlePianoRollLivePreview}
          stepCount={stepCount}
        />
      )}
    </div>
  );
};

export default BeatSeq;
